import Select from '@/common/components/Select'
import { Mapper } from '@/common/services/mapper'
import { ProductData } from '@/common/types/product.type'
import { formatCurrency } from '@/common/utils/formatters'
import { unFormatCurrency, unFormatUnknownUnit } from '@/common/utils/unFormatters'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { ConsumptionValidationSchema } from '../schemas/create.schema'
import ProductItem from './ProductItem'
import { numberToFixedHalfDown } from '@/common/utils/numberToFixedHalfDown';
import { useCardApplicableProducts } from '@/features/consumption/hooks/useCardApplicableProducts'
import useDebounce from '@/common/hooks/useDebounce'

const ProductsForm = () => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<ConsumptionValidationSchema>()

  const {
    append: appendLineItem,
    remove: removeLineItem,
  } = useFieldArray({
    control,
    name: 'line_items_attributes',
  })

  const watchProducts = watch('line_items_attributes') || []
  const cardId = watch("card_id")
  const cardAvailableToConsume = watch("card_available_to_consume")

  const [searchProducts, setSearchProducts] = useDebounce('');

  const { data: products } = useCardApplicableProducts<ProductData[]>({
    filters: {
      cardId,
      search: searchProducts,
    },
  });

  const handleRemoveProduct = (index: number) => {
    removeLineItem(index)
    updateTotalValues({ index })
  }

  const handleAppendProduct = (option: { value: string; label: string }) => {
    if ((option as { value: string }).value) {
      const product = products?.find((product) => product.id === option.value)

      appendLineItem({
        product_id: option?.value,
        quantity: '',
        unitary_amount: '',
        amount: '0',
        display_name: option?.label,
        unit_for_quantity: product?.unit_for_quantity,
      })
    }
  }

  const updateTotalValues = ({ index = -1 }: { index?: number } = {}) => {
    if (watchProducts?.length) {
      let updatedWatchProducts = [...watchProducts]

      if (index >= 0) {
        updatedWatchProducts = updatedWatchProducts.filter((_, i) => i !== index)
      }

      const totalQuantity = updatedWatchProducts.reduce((acc: number, product: any) => {
        return acc + (unFormatUnknownUnit(product?.quantity) || 0)
      }, 0)

      const totalAmount = updatedWatchProducts.reduce((acc: number, product: any) => {
        return (
          acc +
          Number(numberToFixedHalfDown((unFormatUnknownUnit(product?.quantity) || 0) * (Number(unFormatCurrency(product?.unitary_amount)) || 0), 2))
        )
      }, 0)

      setValue('total_quantity', totalQuantity)
      setValue('total_amount', totalAmount)

      updatedWatchProducts.forEach((product: any, index: number) => {
        const totalProductAmount =
          (unFormatUnknownUnit(product?.quantity) || 0) * (Number(unFormatCurrency(product.unitary_amount)) || 0)

        setValue(`line_items_attributes.${index}.amount`, formatCurrency(Number(numberToFixedHalfDown(totalProductAmount, 2))))
      })
    }
  }

  return (
    <div className="flex flex-col gap-3 sm:gap-6 h-full">
      <p className="text-lg sm:text-[20px] font-bold">Produtos transacionados</p>

      <div className="flex flex-col gap-3 sm:gap-6 h-full sm:bg-white-100 sm:p-6 sm:rounded-sm">
        <div className="flex flex-col gap-2">
          <Select
            name='search_products'
            placeholder="Pesquisar produtos"
            options={Mapper.mapToOptions<ProductData>({
              labelFieldName: 'display_name',
              valueFieldName: 'id',
            })(
              products?.filter(
                (product) => !watchProducts?.some((p) => p.product_id === product.id)
              ) ?? []
            )}
            value=""
            onChange={(option) => handleAppendProduct(option as { value: string; label: string })}
            noOptionsMessage={
              cardId ? () => 'Este estabelecimento não possui produtos autorizados para este consumo.' : undefined
            }
            onInputChange={setSearchProducts}
            isDisabled={!cardId || !cardAvailableToConsume}
          />

          {errors?.line_items_attributes && (
            <p className="text-[14px] text-danger-soft">{errors?.line_items_attributes?.message}</p>
          )}
        </div>

        <div className="flex flex-col gap-3 sm:gap-4 overflow-y-auto">
          {watchProducts.map((field, index) => (
            <ProductItem
              key={field.product_id + index}
              index={index}
              field={field}
              updateTotalValues={updateTotalValues}
              onDelete={() => handleRemoveProduct(index)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default ProductsForm
